import { useState } from 'react';
import { Animation, Avatar, Colors, FloatingBox, TooltipWrapper, Typography, kebabCaseAriaAttrs, useClickOutside, } from '@zapier/design-system';
import { isolateComponent, labelStyles } from '@zapier/style-encapsulation';
import { buildAccountSwitcherUrl } from '@zapier/identity';
import { createUniqueClassName } from '../../utils/createUniqueClassName';
import UniversalNavLink from '../UniversalNavLink';
import { AccountsMenuLinks } from '../../constants';
const detailsClassName = createUniqueClassName('AccountsMenu', 'details');
const Styles = labelStyles('AccountsMenu', {
    root: {
        position: 'relative',
    },
    accounts: {
        borderBottom: `1px solid ${Colors.neutral300}`,
        marginBottom: 10,
        paddingBottom: 10,
        overflow: 'auto',
        maxHeight: 'calc(60px * 3.5)',
    },
    summary: {
        position: 'relative',
        display: 'block',
        cursor: 'pointer',
        transition: Animation.transitionValue,
        '&::-webkit-details-marker': {
            display: 'none',
        },
    },
    menuContainer: {
        padding: 10,
        width: 300,
    },
    email: Object.assign(Object.assign({ display: 'flex', alignItems: 'center', justifyContent: 'center', textAlign: 'center', padding: '0 10px' }, Typography.smallPrint1Semibold), { color: Colors.neutral600, marginBottom: 10 }),
    emailText: {
        whiteSpace: 'nowrap',
        textOverflow: 'ellipsis',
        overflow: 'hidden',
    },
});
const renderAccountSwitchMenu = (props, setIsMenuOpen) => (isolateComponent("div", { css: Styles.accounts }, props.accounts.map((account) => {
    const isCurrentAccount = account.id === props.currentAccountId;
    return (isolateComponent(UniversalNavLink, { href: buildAccountSwitcherUrl(account.id), isSelected: isCurrentAccount, key: account.id, onClick: (event) => {
            if (props.onAccountClick) {
                props.onAccountClick(account, event);
            }
            setIsMenuOpen(false);
        }, renderBeforeText: () => (isolateComponent("div", { css: Styles.avatar },
            isolateComponent(Avatar, { isBlock: true, isSelected: isCurrentAccount, name: account.name, shouldLazilyLoad: false, size: 40, url: account.imageUrl }))), role: "menuitem", text: account.name }));
})));
const renderMenuLinks = (props, setIsMenuOpen) => props.menuLinks.map((menuLink) => (isolateComponent(UniversalNavLink, { href: menuLink.href, icon: menuLink.icon, key: menuLink.text, onClick: () => setIsMenuOpen(false), role: "menuitem", text: menuLink.text, target: menuLink.target })));
const fallbackAccount = {
    id: -1,
    name: '⋯',
};
const AccountsMenu = (props) => {
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [setClickOutsideNode] = useClickOutside({
        onClickOutside: () => setIsMenuOpen(false),
    });
    const currentAccount = props.accounts.find((account) => account.id === props.currentAccountId) ||
        fallbackAccount;
    const toggleMenu = (event) => {
        event.preventDefault();
        setIsMenuOpen(!isMenuOpen);
    };
    return (isolateComponent("div", { css: Styles.root },
        isolateComponent("details", { className: detailsClassName, "data-testid": "accountsMenu", open: isMenuOpen, ref: (node) => setClickOutsideNode(node) },
            isolateComponent("summary", { "aria-haspopup": "menu", "aria-label": props.menuLabel, css: Styles.summary, "data-testid": "accounts-menu-summary", onClick: toggleMenu, tabIndex: 0 },
                isolateComponent(TooltipWrapper, { content: currentAccount.name, isBlock: true, position: "west", shouldCorrectPosition: false }, ({ childProps }) => (isolateComponent("div", Object.assign({}, kebabCaseAriaAttrs(childProps)),
                    isolateComponent(Avatar, { isBlock: true, isInteractive: true, isSelected: isMenuOpen, name: currentAccount.name, shouldLazilyLoad: false, size: props.size === 'small' ? 25 : 40, url: currentAccount.imageUrl }))))),
            isolateComponent(FloatingBox, { align: "right", hasWhiteBackground: props.hasWhiteBackground, position: "south", borderRadius: 10 },
                isolateComponent("div", { css: Styles.menuContainer, role: "menu" },
                    isolateComponent("div", { css: Styles.email },
                        isolateComponent("span", { css: Styles.emailText }, props.email)),
                    props.accounts.length > 1 &&
                        renderAccountSwitchMenu(props, setIsMenuOpen),
                    renderMenuLinks(props, setIsMenuOpen))))));
};
AccountsMenu.defaultProps = {
    menuLabel: 'Accounts Settings',
    menuLinks: AccountsMenuLinks,
    size: 'medium',
};
export default AccountsMenu;
//# sourceMappingURL=AccountsMenu.js.map
// @zapier/universal-layout@5.12.61 (4902ab0c1)
